import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import * as authApi from '../api/auth';
import { User, UserRole, LoginPayload, RegisterPayload } from '../api/auth';

interface DecodedToken {
  userId: number;
  email: string;
  role: UserRole;
  tokenVersion: number;
  exp: number;
  iat: number;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginPayload) => Promise<void>;
  register: (data: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes (tokens expire in 15)

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if token is expired or about to expire
  const isTokenExpired = (token: string): boolean => {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const now = Date.now() / 1000;
      return decoded.exp < now;
    } catch {
      return true;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (storedAccessToken && !isTokenExpired(storedAccessToken)) {
          // Token is valid, fetch user info
          setAccessToken(storedAccessToken);
          const userData = await authApi.getCurrentUser(storedAccessToken);
          setUser(userData);
          console.debug('[AuthProvider] Initialized from stored token', { userId: userData.id });
        } else if (storedRefreshToken && !isTokenExpired(storedRefreshToken)) {
          // Access token expired but refresh token valid
          console.debug('[AuthProvider] Access token expired, refreshing...');
          const response = await authApi.refresh({ refreshToken: storedRefreshToken });
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          setAccessToken(response.accessToken);
          setUser(response.user);
          console.debug('[AuthProvider] Token refreshed on init', { userId: response.user.id });
        } else {
          // No valid tokens, clear storage
          console.debug('[AuthProvider] No valid tokens found');
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      } catch (err) {
        console.error('[AuthProvider] Failed to initialize auth', err);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (!accessToken) return;

    const interval = setInterval(async () => {
      try {
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!storedRefreshToken) {
          console.warn('[AuthProvider] No refresh token available');
          return;
        }

        console.debug('[AuthProvider] Auto-refreshing token...');
        const response = await authApi.refresh({ refreshToken: storedRefreshToken });
        localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        setAccessToken(response.accessToken);
        setUser(response.user);
        console.debug('[AuthProvider] Token auto-refreshed', { userId: response.user.id });
      } catch (err) {
        console.error('[AuthProvider] Auto-refresh failed', err);
        // If refresh fails, clear state and redirect
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setAccessToken(null);
        setUser(null);
      }
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const login = async (credentials: LoginPayload): Promise<void> => {
    try {
      const response = await authApi.login(credentials);
      localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
      setAccessToken(response.accessToken);
      setUser(response.user);
      console.info('[AuthProvider] User logged in', { userId: response.user.id });
    } catch (err) {
      console.error('[AuthProvider] Login failed', err);
      throw err;
    }
  };

  const register = async (data: RegisterPayload): Promise<void> => {
    try {
      const response = await authApi.register(data);
      localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
      setAccessToken(response.accessToken);
      setUser(response.user);
      console.info('[AuthProvider] User registered', { userId: response.user.id });
    } catch (err) {
      console.error('[AuthProvider] Registration failed', err);
      throw err;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (accessToken) {
        await authApi.logout(accessToken);
      }
    } catch (err) {
      console.error('[AuthProvider] Logout API call failed', err);
    } finally {
      // Always clear local state and storage
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setAccessToken(null);
      setUser(null);
      console.info('[AuthProvider] User logged out');
    }
  };

  const refreshTokenManual = async (): Promise<void> => {
    try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authApi.refresh({ refreshToken: storedRefreshToken });
      localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
      setAccessToken(response.accessToken);
      setUser(response.user);
      console.debug('[AuthProvider] Token manually refreshed', { userId: response.user.id });
    } catch (err) {
      console.error('[AuthProvider] Manual refresh failed', err);
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    login,
    register,
    logout,
    refreshToken: refreshTokenManual,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
